import { useEffect, useState } from 'react';
import type { SessionResponse, TenantSummary } from '@cusp/contracts';
import { adoptSessionIdentity, fetchSession, logout } from '../session.js';
import { useIdentity } from '../useIdentity.js';

/**
 * The session logic behind the header's identity affordance, as a headless hook
 * so more than one presentation can share it: the learner app's account card
 * and the school-admin console's rail card both read it. Reused rather than
 * reinvented — the fetch-once-before-any-identity dance and the "no membership
 * selected" edge are subtle enough that two copies would drift.
 */
export type SessionControlsState =
  | { status: 'loading' }
  /** No live session — the caller falls back to the dev identity affordance. */
  | { status: 'anon' }
  | {
      status: 'authed';
      session: SessionResponse;
      /** The membership matching the acting identity, if one is selected. */
      acting: TenantSummary | undefined;
      /** More than one membership, so a tenant switch is meaningful. */
      canSwitch: boolean;
    };

/**
 * Resolve the current session and the acting membership.
 *
 * Deliberately not react-query: this runs before any identity exists, so an
 * identity-scoped key would be wrong, and caching "who am I" across a
 * login/logout boundary is exactly the staleness the cache-partitioning design
 * exists to avoid. A network/server failure resolves to `anon` rather than
 * wedging the header on a spinner.
 */
export function useSessionControls(): SessionControlsState {
  const identity = useIdentity();
  const [session, setSession] = useState<SessionResponse | null | undefined>(undefined);

  useEffect(() => {
    let alive = true;
    fetchSession().then(
      (s) => {
        if (!alive) return;
        if (s) adoptSessionIdentity(s);
        setSession(s);
      },
      () => {
        if (alive) setSession(null);
      },
    );
    return () => {
      alive = false;
    };
  }, []);

  if (session === undefined) return { status: 'loading' };
  if (session === null) return { status: 'anon' };

  const acting =
    identity === null
      ? undefined
      : session.memberships.find((m) => m.tenantId === identity.tenantId);

  return { status: 'authed', session, acting, canSwitch: session.memberships.length > 1 };
}

/**
 * Revoke the session server-side and drop local credentials, then hop to the
 * login screen. Shared by both presentations so "sign out" means one thing.
 */
export function signOut(loginPath = '/login'): void {
  void logout().then(() => {
    window.location.assign(loginPath);
  });
}
